import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { TrialBalanceType, type TrialBalanceDetail } from '@/core/models/trial-balance.models';
import { notifyError } from '@/core/notify/notify';
import { matrixPath } from '@/core/routing/matrix';
import { trialBalanceService } from '@/core/services/trialBalance';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { EmptyState } from '@/kit/EmptyState';
import { Icon } from '@/kit/Icon';
import { PageHeader } from '@/kit/PageHeader';
import { TB_EDIT_BASE, TB_EDIT_SEGMENT, TB_LIST_ROUTE, displayDate } from './trialBalanceEditor';
import { buildTrialBalanceView } from './trialBalanceView';
import './TaskTablePage.scss';
import './TaxreturnViewTrialBalance.scss';

const money = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** A nil side prints as a dash, as on a printed trial balance. */
const amount = (value: number) => (Math.abs(value) < 0.005 ? '-' : money.format(value));

/**
 * APT366 (client: "when the user clicks a TB reference number, the system should display the corresponding Trial Balance in a view
 * similar to the Accounts Report"): a saved trial balance, read only, laid out like a printed statement - Profit and loss and Balance
 * sheet, each account type with its subtotal, then the totals. Edit opens the editor; the pencil on the list still goes there directly.
 */
export function TaxreturnViewTrialBalance() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<TrialBalanceDetail | null>(null);
  /** A load failure is toasted and redirected once (React StrictMode runs the load twice in development). */
  const loadFailed = useRef(false);

  useEffect(() => {
    let cancelled = false;
    trialBalanceService.getTrialBalance(id).then(
      (result) => {
        if (!cancelled) setDetail(result);
      },
      (error) => {
        if (cancelled || loadFailed.current) return;
        loadFailed.current = true;
        notifyError(error);
        navigate(TB_LIST_ROUTE);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const [exporting, setExporting] = useState(false);
  const sections = useMemo(() => (detail ? buildTrialBalanceView(detail) : []), [detail]);

  const onEdit = () => navigate(matrixPath(TB_EDIT_BASE, TB_EDIT_SEGMENT, { id: btoa(id) }));
  /** format: 1 CSV, 3 PDF (the list's own export). */
  const onExport = (format: number) => {
    if (exporting) return;
    setExporting(true);
    trialBalanceService
      .exportTrialBalance(id, format)
      .catch((error) => notifyError(error))
      .finally(() => setExporting(false));
  };

  const period = detail ? [displayDate(detail.fromDate), displayDate(detail.toDate)].filter(Boolean).join(' to ') : '';

  return (
    <SideMenuPage menu="task" contentClassName="tr-table-page tb-view-page">
      <div className="content-body tb-view">
        {/* Page actions top-right in the sticky header (design review 2 Nov 2026). */}
        <PageHeader title={detail ? `Trial Balance ${detail.refNo}` : 'Trial Balance'}>
          <Button className="action-button secondary-button" onClick={() => navigate(TB_LIST_ROUTE)}>
            <Icon name="arrow_back" outlined />
            <span>Back to list</span>
          </Button>
          {detail && (
            <>
              <Button className="action-button secondary-button" title="Download as PDF" busy={exporting} onClick={() => onExport(3)}>
                <Icon name="picture_as_pdf" outlined className="icon-pdf" />
                <span>PDF</span>
              </Button>
              <Button className="action-button secondary-button" title="Download as CSV" busy={exporting} onClick={() => onExport(1)}>
                <Icon name="description" outlined className="icon-csv" />
                <span>CSV</span>
              </Button>
              <Button className="action-button primary-button" onClick={onEdit}>
                <Icon name="edit" outlined />
                <span>Edit</span>
              </Button>
            </>
          )}
        </PageHeader>

        {!detail ? (
          <p className="tb-view__loading" role="status">
            Loading the trial balance...
          </p>
        ) : (
          <>
            <dl className="tb-view__summary" aria-label="Trial balance details">
              <div>
                <dt>Client</dt>
                <dd>{detail.clientName}</dd>
              </div>
              <div>
                <dt>Period</dt>
                <dd>{period || '-'}</dd>
              </div>
              <div>
                <dt>Type</dt>
                <dd>{Number(detail.typeId) === TrialBalanceType.ManagementOnly ? 'Management only' : 'Annual'}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd className={detail.isBalanced ? 'text-green' : 'text-red'}>
                  {detail.isBalanced ? 'Balanced' : `Out of balance by £ ${money.format(Math.abs(detail.difference))}`}
                </dd>
              </div>
              {detail.description && (
                <div className="tb-view__summary-wide">
                  <dt>Description</dt>
                  <dd>{detail.description}</dd>
                </div>
              )}
            </dl>

            {sections.length === 0 ? (
              <EmptyState
                icon="description"
                title="This trial balance has no lines"
                hint="Use Edit to add the account lines."
              />
            ) : (
              <table className="tb-view__table">
                <thead>
                  <tr>
                    <th scope="col">Code</th>
                    <th scope="col">Account</th>
                    <th scope="col" className="tb-view__amount">
                      Debit (£)
                    </th>
                    <th scope="col" className="tb-view__amount">
                      Credit (£)
                    </th>
                  </tr>
                </thead>
                {sections.map((section) => (
                  <tbody key={section.key} data-testid={`tb-section-${section.key}`}>
                    <tr className="tb-view__section">
                      <th colSpan={4} scope="colgroup">
                        {section.title}
                      </th>
                    </tr>
                    {section.groups.map((group) => (
                      <Fragment key={group.key}>
                        <tr className="tb-view__group">
                          <th colSpan={4} scope="colgroup">
                            {group.title}
                          </th>
                        </tr>
                        {group.lines.map((line) => (
                          <tr key={`${group.key}:${line.lineNo}:${line.code}`} className="tb-view__line">
                            <td className="tb-view__code">{line.code}</td>
                            <td>
                              {line.name}
                              {line.particular && <span className="tb-view__particular">{line.particular}</span>}
                            </td>
                            <td className="tb-view__amount">{amount(line.debit)}</td>
                            <td className="tb-view__amount">{amount(line.credit)}</td>
                          </tr>
                        ))}
                        <tr className="tb-view__subtotal">
                          <td colSpan={2}>Total {group.title}</td>
                          <td className="tb-view__amount">{amount(group.debit)}</td>
                          <td className="tb-view__amount">{amount(group.credit)}</td>
                        </tr>
                      </Fragment>
                    ))}
                    {sections.length > 1 && (
                      <tr className="tb-view__section-total">
                        <td colSpan={2}>Total {section.title.toLowerCase()}</td>
                        <td className="tb-view__amount">{amount(section.debit)}</td>
                        <td className="tb-view__amount">{amount(section.credit)}</td>
                      </tr>
                    )}
                  </tbody>
                ))}
                <tfoot>
                  <tr className="tb-view__total">
                    <th colSpan={2} scope="row">
                      Total
                    </th>
                    <td className="tb-view__amount">{amount(detail.totalDebit)}</td>
                    <td className="tb-view__amount">{amount(detail.totalCredit)}</td>
                  </tr>
                  {!detail.isBalanced && (
                    <tr className="tb-view__difference">
                      <th colSpan={2} scope="row">
                        Difference
                      </th>
                      <td className="tb-view__amount" colSpan={2}>
                        {money.format(Math.abs(detail.difference))}
                      </td>
                    </tr>
                  )}
                </tfoot>
              </table>
            )}
          </>
        )}
      </div>
    </SideMenuPage>
  );
}
